import {
  DateInputChangeEvent,
  DateInputProps,
  DatePicker,
} from '@progress/kendo-react-dateinputs';
import { ErrorDisplay } from 'Components/Display';
import React, { useEffect, useState } from 'react';
import './DatePicker.scss';
import { formatDateToUtc, parseUtcToLocal } from 'Utilities/DateTimeUtc';

interface DatePickerInputProps {
  label?: string;
  value: Date | string; // Can accept Date object or UTC string from backend
  onChange: (e: any) => void;
  required?: boolean;
  error?: string;
  hint?: string;
  isTable?: boolean;
}

const parts = [
  { length: 2, sep: '/' }, // MM
  { length: 2, sep: '/' }, // DD
  { length: 4, sep: '' }, // YYYY
];

const formatWithMask = (digits: string) => {
  let result = '';
  let idx = 0;
  for (let part of parts) {
    const take = Math.min(part.length, digits.length - idx);
    if (take <= 0) break;
    result += digits.substr(idx, take);
    idx += take;
    if (take === part.length && part.sep) {
      result += part.sep;
    }
  }
  return result;
};

export const DateInput = (props: DateInputProps) => {
  const { value, onChange } = props;
  const [raw, setRaw] = useState<string>('');

  useEffect(() => {
    if (value instanceof Date && !isNaN(value.getTime())) {
      const mm = String(value.getMonth() + 1).padStart(2, '0');
      const dd = String(value.getDate()).padStart(2, '0');
      const yyyy = String(value.getFullYear()).padStart(4, '0');
      setRaw((mm + dd + yyyy).substr(0, 8));
    } else {
      setRaw('');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').substr(0, 8);
    setRaw(digits);

    if (digits.length === 8 && onChange) {
      const [mm, dd, yyyy] = [
        digits.substr(0, 2),
        digits.substr(2, 2),
        digits.substr(4, 4),
      ];
      const month = Number(mm);
      const day = Number(dd);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const newDate = new Date(Number(yyyy), month - 1, day);
        if (!isNaN(newDate.getTime())) {
          const changeEvent = {
            syntheticEvent: e,
            nativeEvent: e.nativeEvent,
            value: newDate,
            target: { value: newDate },
          } as any;
          onChange(changeEvent);
        }
      }
    }
  };

  return (
    <input
      type="text"
      style={{ border: 'none', width: '100%' }}
      className="k-textbox k-input"
      value={formatWithMask(raw)}
      onChange={handleChange}
      placeholder="MM/DD/YYYY"
    />
  );
};

export const DatePickerInput = (props: DatePickerInputProps) => {
  const { label, required, value, onChange, error, hint, isTable } = props;

  // Helper to convert value to Date, handling both Date objects and UTC strings
  const convertValueToDate = (val: Date | string | undefined): Date | undefined => {
    if (!val) return undefined;

    // If it's a string in backend UTC format, parse it as UTC
    if (typeof val === 'string') {
      const utcDate = parseUtcToLocal(val);
      return utcDate || undefined;
    }

    // If it's a Date object, use it directly (already in local time)
    return val;
  };

  const [dateValue, setDateValue] = useState<Date | undefined>(
    convertValueToDate(value)
  );

  // Update dateValue when value prop changes (e.g., when form loads data from backend)
  useEffect(() => {
    setDateValue(convertValueToDate(value));
  }, [value]);

  const onDateChange = (e: any) => {
    const selected = e.target.value;
    setDateValue(selected);
    console.log('Selected date:', selected);
    console.log('Selected date UTC:', formatDateToUtc(selected, true));
    onChange(formatDateToUtc(selected, true)); // true for dateOnly (midnight UTC)
  };

  return (
    <div className={`date-picker-wrapper ${isTable && 'table-wrapper'}`}>
      {label && (
        <span className="date-picker-label">
          {label} {required && <span className="required">*</span>}
        </span>
      )}
      <div className="date-picker-row">
        <DatePicker
          dateInput={DateInput}
          value={dateValue}
          onChange={onDateChange}
          format={'MM/dd/yyyy'}
          className={`${error ? 'with-error' : ''}`}
        />
        {hint && !error && <div className="hint">{hint}</div>}
        <ErrorDisplay errors={[error ?? '']} />
      </div>
    </div>
  );
};
